import { ConfigBandwidthTemplate } from './config-bandwidth-template';
import { ConfigGenericTemplate } from './config-generic-template';
import { MlyticsConfigTemplate } from './mlytics-config-template';
import { OtherStandardTemplate } from './other-standard-template';
import { DomainTemplate } from './domain/domain-template';
import { VCloudTemplate } from './vcloud/vcloud-template';
import { CdnTemplate } from './cdn/cdn-template';
import { SslTemplate } from './ssl/ssl-template';
import { TranscodingTemplate } from './transcoding/transcoding-template';
import { ServerVbmTemplate } from './server-vbm/server-vbm-template';

/**
 * Per-product-type templates, keyed by the product's `template_name` (falling
 * back to the product group). Each writes into
 * `products.{i}.service_configoptions` / `.service_domain_metadata`.
 *
 * Tier 1 (Config.Bandwidth / Config.Config / Mlytics-family / Other.Standard)
 * plus the Phase-D deep templates (Domain, VCloud, CDN, SSL, Transcoding,
 * Server.VBM) are all wired below, mirroring the legacy `template.ts` map.
 */

/** template_name → generic (Mlytics) config template. Legacy fallback set. */
const MLYTICS_TEMPLATES = new Set([
  'Cloud.VPS',
  'Cloud.Hosting',
  'Email.Server',
  'Email.EGPlatform',
  'Mlytics.Vnis',
  'Server.Colocation',
]);

/** SwiftFederation aliases that all share the Transcoding builder (legacy map). */
const TRANSCODING_TEMPLATES = new Set([
  'SwiftFederation.Transcoding',
  'SwiftFederation.Recording',
  'SwiftFederation.StorageOSS',
  'SwiftFederation.StorageLFD',
  'SwiftFederation.Transmuxing',
]);

/** Normalise the OTHER/KHÁC group to the Other.Standard template. */
function isOtherGroup(group?: string): boolean {
  const g = (group ?? '').trim().toUpperCase();
  return g === 'OTHER' || g === 'KHÁC' || g === 'KHAC';
}

/**
 * Renders the per-type pricing template below the base product fields, chosen
 * from the selected product's `template_name` / group. Unknown/base types (and
 * the not-yet-loaded state) render nothing extra.
 */
export function ProductTemplateDispatcher({
  index,
  templateName,
  productGroupName,
  detail,
}: {
  index: number;
  templateName: string;
  productGroupName: string;
  detail: unknown;
}) {
  const props = { index, templateName, detail };

  // OTHER group is always Other.Standard (legacy forced override).
  if (isOtherGroup(productGroupName) || templateName === 'Other.Standard') {
    return <OtherStandardTemplate index={index} />;
  }

  // Tier 1.
  if (templateName === 'Config.Bandwidth') return <ConfigBandwidthTemplate index={index} />;
  if (templateName === 'Config.Config') return <ConfigGenericTemplate index={index} />;
  if (MLYTICS_TEMPLATES.has(templateName)) {
    return <MlyticsConfigTemplate index={index} detail={detail} />;
  }

  // Phase D per-type deep templates.
  if (templateName === 'Domain.TLDs' || templateName === 'Domain.VNNIC') {
    return <DomainTemplate {...props} />;
  }
  if (templateName === 'Cloud.VCloud') return <VCloudTemplate {...props} />;
  if (templateName === 'SwiftFederation.CDN') return <CdnTemplate {...props} />;
  if (templateName === 'SSL.SSL') return <SslTemplate {...props} />;
  if (TRANSCODING_TEMPLATES.has(templateName)) return <TranscodingTemplate {...props} />;
  if (templateName === 'Server.VBM' || templateName === 'Device.Liquidation') {
    return <ServerVbmTemplate {...props} />;
  }

  return null;
}
